import React, { useState, useEffect, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { WorkoutSession, WeeklySplit, MesocycleWeek } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Flame, Edit2, Trash2, Heart, Activity, Plus, Eye, CheckCircle2, Clock, Moon, Zap, Dumbbell, Target, ArrowUp, ArrowDown, Layers, Zap as ZapIcon } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths, subMonths, startOfWeek, subWeeks, addDays, subDays } from 'date-fns';
import { toast } from 'sonner';
import SplitManager from './SplitManager';
import SetTypeBadge, { getSetTypeBadgeConfig } from './SetTypeBadge';
import EditCompletedSessionDialog from './EditCompletedSessionDialog';
import DayStatsModal from './DayStatsModal';
import { useClientContext } from '@/contexts/ClientContext';
import { ForgePill, ForgeSectionHeader, type ForgeColor, forgeColor } from '@/components/ui/ForgeUI';

interface WorkoutCalendarProps {
  selectedDate: string; // YYYY-MM-DD
  onSelectDate: (date: string) => void;
  onAddNewWorkout?: (date: string) => void; // Create a fresh draft on a date that already has a session
  refreshTrigger: number;
  fullWidth?: boolean;
}

export default function WorkoutCalendar({ selectedDate, onSelectDate, onAddNewWorkout, refreshTrigger, fullWidth }: WorkoutCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Day-action dialog state
  const [dayActionDate, setDayActionDate] = useState<string | null>(null);
  const [dayActionSession, setDayActionSession] = useState<(WorkoutSession & { watchCalories?: number | null; watchAvgHr?: number | null; watchMaxHr?: number | null }) | null>(null);

  // Muscle group filter history dialog state
  const [muscleFilterGroup, setMuscleFilterGroup] = useState<string | null>(null);

  // Day Stats Modal (long-press / right-click)
  const [dayStatsDate, setDayStatsDate] = useState<string | null>(null);
  const [dayStatsSession, setDayStatsSession] = useState<(WorkoutSession & { watchCalories?: number | null; watchAvgHr?: number | null; watchMaxHr?: number | null; sessionFeeling?: string | null; recoveryNudge?: string | null }) | null>(null);

  const openDayStats = (dateStr: string, session: typeof dayStatsSession) => {
    setDayStatsDate(dateStr);
    setDayStatsSession(session ?? null);
    // Haptic feedback on mobile
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  const closeDayStats = () => {
    setDayStatsDate(null);
    setDayStatsSession(null);
  };

  const utils = trpc.useUtils();
  const { activeClient, isViewingClient } = useClientContext();
  const clientId = activeClient?.clientId;

  // When in client view, use trainer portal procedures; otherwise use self-scoped queries
  const { data: rawSessions = [] } = trpc.sessions.list.useQuery(undefined, { enabled: !isViewingClient });
  const { data: clientSessions = [] } = trpc.trainerPortal.getClientSessions.useQuery(
    { clientId: clientId! },
    { enabled: isViewingClient && !!clientId }
  );
  const effectiveSessions = isViewingClient ? clientSessions : rawSessions;

  const { data: rawSplits = [] } = trpc.splits.list.useQuery(undefined, { enabled: !isViewingClient });
  const { data: clientSplits = [] } = trpc.trainerPortal.getClientSplits.useQuery(
    { clientId: clientId! },
    { enabled: isViewingClient && !!clientId }
  );
  const effectiveSplits = isViewingClient ? clientSplits : rawSplits;

  const { data: exercises = [] } = trpc.exercises.list.useQuery();

  // Nutrition month totals for calendar day cards
  const currentYearMonth = useMemo(() => format(currentMonth, 'yyyy-MM'), [currentMonth]);
  const { data: monthNutritionData } = trpc.nutrition.getMonthTotals.useQuery(
    { yearMonth: currentYearMonth },
    { enabled: !isViewingClient }
  );
  const { data: clientNutritionData } = trpc.trainerPortal.getClientNutritionMonthTotals.useQuery(
    { clientId: clientId!, yearMonth: currentYearMonth },
    { enabled: isViewingClient && !!clientId }
  );
  const effectiveNutritionData = isViewingClient ? clientNutritionData : monthNutritionData;
  // Server now returns { daily: Record<date, macros>, targets: { protein, carbs, fat, calories } }
  const monthNutrition = effectiveNutritionData?.daily ?? {};
  const macroTargets = effectiveNutritionData?.targets ?? { protein: 150, carbs: 200, fat: 65, calories: 2000 };

  const sessions: (WorkoutSession & { watchCalories?: number | null; watchAvgHr?: number | null; watchMaxHr?: number | null; sessionFeeling?: string | null; recoveryNudge?: string | null })[] = effectiveSessions.map((s) => ({
    id: s.id,
    date: s.date,
    name: s.name,
    plannedExercises: (s.plannedExercises as any[]) || [],
    completedAt: s.completedAt ?? undefined,
    isLive: s.isLive ?? undefined,
    notes: s.notes ?? undefined,
    watchCalories: (s as any).watchCalories ?? null,
    watchAvgHr: (s as any).watchAvgHr ?? null,
    watchMaxHr: (s as any).watchMaxHr ?? null,
    sessionFeeling: (s as any).sessionFeeling ?? null,
    recoveryNudge: (s as any).recoveryNudge ?? null,
  }));

  const activeSplit: WeeklySplit = (() => {
    const raw = effectiveSplits.find((s) => s.isActive) || effectiveSplits[0];
    if (!raw) {
      return {
        id: 'default',
        name: 'PPL Split',
        isActive: true,
        days: [
          { dayIdx: 0, name: 'Monday', category: 'Push' },
          { dayIdx: 1, name: 'Tuesday', category: 'Pull' },
          { dayIdx: 2, name: 'Wednesday', category: 'Legs' },
          { dayIdx: 3, name: 'Thursday', category: 'Push' },
          { dayIdx: 4, name: 'Friday', category: 'Pull' },
          { dayIdx: 5, name: 'Saturday', category: 'Legs' },
          { dayIdx: 6, name: 'Sunday', category: 'Rest' },
        ],
      };
    }
    return {
      id: raw.id,
      name: raw.name,
      isActive: raw.isActive,
      isMesocycle: !!(raw as any).isMesocycle,
      weeks: (raw as any).weeks as MesocycleWeek[] | undefined,
      days: (raw.days as any[]).map((d: any) => ({
        dayIdx: d.dayIdx,
        name: d.name,
        category: d.category,
      })),
    };
  })();

  const saveSessionMutation = trpc.sessions.save.useMutation({
    onSuccess: () => utils.sessions.list.invalidate(),
  });

  const deleteSessionMutation = trpc.sessions.delete.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
      utils.prs.list.invalidate();
    },
  });

  const handleDeleteSession = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the completed session "${name}"? This will permanently remove its metrics and associated PRs.`)) {
      await deleteSessionMutation.mutateAsync({ sessionId: id });
      toast.success(`Deleted session "${name}"`);
    }
  };

  const handleSplitChange = () => {
    utils.splits.list.invalidate();
  };

  const handleGenerateWeekFromSplit = async () => {
    const currentWeekStart = startOfWeek(new Date(selectedDate + 'T00:00:00'), { weekStartsOn: 1 });

    if (!confirm(`Generate workouts for this entire week (${format(currentWeekStart, 'MMM d')} - ${format(addDays(currentWeekStart, 6), 'MMM d')}) using your active split "${activeSplit.name}"? This will overwrite existing planned/uncompleted sessions.`)) {
      return;
    }

    let generatedCount = 0;

    for (let i = 0; i < 7; i++) {
      const targetDay = addDays(currentWeekStart, i);
      const targetDayStr = format(targetDay, 'yyyy-MM-dd');
      const splitDay = activeSplit.days[i];

      if (splitDay.category.toLowerCase() === 'rest') continue;

      const hasCompletedSession = sessions.some((s) => s.date === targetDayStr && !!s.completedAt);
      if (hasCompletedSession) continue;

      const existingDraft = sessions.find((s) => s.date === targetDayStr && !s.completedAt);
      if (existingDraft) {
        await deleteSessionMutation.mutateAsync({ sessionId: existingDraft.id });
      }

      await saveSessionMutation.mutateAsync({
        id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        date: targetDayStr,
        name: `${splitDay.category} Session`,
        plannedExercises: [],
        notes: `Auto-generated from weekly split: ${splitDay.category}`,
      });

      generatedCount++;
    }

    toast.success(`Successfully generated ${generatedCount} split workouts for this week!`);
    onSelectDate(selectedDate);
  };

  const handleApplyMesocycle = async () => {
    if (!activeSplit.isMesocycle || !activeSplit.weeks || activeSplit.weeks.length !== 4) {
      toast.error('Active split is not a mesocycle. Enable 4-Week Mode in the split editor first.');
      return;
    }
    const currentWeekStart = startOfWeek(new Date(selectedDate + 'T00:00:00'), { weekStartsOn: 1 });
    const endDate = addDays(currentWeekStart, 27);
    if (!confirm(`Apply full 4-week mesocycle starting ${format(currentWeekStart, 'MMM d')} through ${format(endDate, 'MMM d')}? This will overwrite uncompleted sessions across all 4 weeks.`)) return;

    let generatedCount = 0;
    for (let weekIdx = 0; weekIdx < 4; weekIdx++) {
      const mesoWeek = activeSplit.weeks[weekIdx];
      const weekStart = addDays(currentWeekStart, weekIdx * 7);
      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const targetDay = addDays(weekStart, dayIdx);
        const targetDayStr = format(targetDay, 'yyyy-MM-dd');
        const splitDay = mesoWeek.days[dayIdx];
        if (!splitDay || splitDay.category.toLowerCase() === 'rest') continue;
        const hasCompleted = sessions.some((s) => s.date === targetDayStr && !!s.completedAt);
        if (hasCompleted) continue;
        const existingDraft = sessions.find((s) => s.date === targetDayStr && !s.completedAt);
        if (existingDraft) await deleteSessionMutation.mutateAsync({ sessionId: existingDraft.id });
        await saveSessionMutation.mutateAsync({
          id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          date: targetDayStr,
          name: `${splitDay.category} Session`,
          plannedExercises: [],
          notes: `Week ${weekIdx + 1} (${mesoWeek.intent}) — ${activeSplit.name}`,
        });
        generatedCount++;
      }
    }
    toast.success(`Mesocycle applied — ${generatedCount} sessions created across 4 weeks!`);
    onSelectDate(selectedDate);
  };

  const handleCopyLastWeek = async () => {
    const currentWeekStart = startOfWeek(new Date(selectedDate + 'T00:00:00'), { weekStartsOn: 1 });
    const lastWeekStart = subDays(currentWeekStart, 7);
    const lastWeekEnd = subDays(currentWeekStart, 1);

    const lastWeekStartStr = format(lastWeekStart, 'yyyy-MM-dd');
    const lastWeekEndStr = format(lastWeekEnd, 'yyyy-MM-dd');

    if (!confirm(`Copy all planned workouts from last week (${format(lastWeekStart, 'MMM d')} - ${format(lastWeekEnd, 'MMM d')}) to the current week?`)) {
      return;
    }

    const lastWeekSessions = sessions.filter((s) => s.date >= lastWeekStartStr && s.date <= lastWeekEndStr);

    if (lastWeekSessions.length === 0) {
      toast.error('No workouts found in last week to copy!');
      return;
    }

    let copyCount = 0;

    for (let i = 0; i < 7; i++) {
      const sourceDay = addDays(lastWeekStart, i);
      const targetDay = addDays(currentWeekStart, i);
      const sourceDayStr = format(sourceDay, 'yyyy-MM-dd');
      const targetDayStr = format(targetDay, 'yyyy-MM-dd');

      const sourceSession = lastWeekSessions.find((s) => s.date === sourceDayStr);
      if (sourceSession) {
        const existingTargetSession = sessions.find((s) => s.date === targetDayStr && !s.completedAt);
        if (existingTargetSession) {
          await deleteSessionMutation.mutateAsync({ sessionId: existingTargetSession.id });
        }

        const copiedPlannedExercises = sourceSession.plannedExercises.map((pe) => ({
          id: `pe-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          exerciseId: pe.exerciseId,
          sets: pe.sets.map((set, sIdx) => ({
            id: `set-${Date.now()}-${Math.random().toString(36).substr(2, 5)}-${sIdx}`,
            reps: set.reps,
            weight: set.weight,
            completed: false,
          })),
        }));

        await saveSessionMutation.mutateAsync({
          id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          date: targetDayStr,
          name: sourceSession.name,
          plannedExercises: copiedPlannedExercises,
          notes: sourceSession.notes,
        });

        copyCount++;
      }
    }

    toast.success(`Successfully copied ${copyCount} workouts to this week!`);
    onSelectDate(selectedDate);
  };

  const getWeeklyStreak = (): number => {
    const completedSessions = sessions.filter((s) => !!s.completedAt);
    if (completedSessions.length === 0) return 0;

    let streak = 0;
    let currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

    while (true) {
      const nextWeekStart = new Date(currentWeekStart);
      nextWeekStart.setDate(nextWeekStart.getDate() + 7);

      const weekSessions = completedSessions.filter((s) => {
        const d = new Date(s.date + 'T00:00:00');
        return d >= currentWeekStart && d < nextWeekStart;
      });

      const isCurrentWeek = currentWeekStart <= new Date() && new Date() < nextWeekStart;

      if (weekSessions.length >= 3) {
        streak++;
      } else if (isCurrentWeek) {
        if (streak === 0) {
          const prevWeekStart = subWeeks(currentWeekStart, 1);
          const prevWeekEnd = currentWeekStart;
          const prevWeekSessions = completedSessions.filter((s) => {
            const d = new Date(s.date + 'T00:00:00');
            return d >= prevWeekStart && d < prevWeekEnd;
          });
          if (prevWeekSessions.length >= 3) {
            // keep streak alive
          } else {
            break;
          }
        }
      } else {
        break;
      }

      currentWeekStart = subWeeks(currentWeekStart, 1);
    }

    return streak;
  };

  const streak = getWeeklyStreak();

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const startDayOfWeek = monthStart.getDay();
  const paddingDays = Array.from({ length: startDayOfWeek });

  const getSessionForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return sessions.find((s) => s.date === dateStr);
  };

  const handleDayClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingSession = getSessionForDate(date);
    if (existingSession) {
      // Show action dialog so user can choose to view/edit or add another
      setDayActionDate(dateStr);
      setDayActionSession(existingSession);
    } else {
      // Empty day — go straight to planner
      onSelectDate(dateStr);
    }
  };

  const weekdayHeaders = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    const dateToUse = selectedDate.includes('T') ? new Date(selectedDate) : new Date(selectedDate + 'T00:00:00');
    return startOfWeek(dateToUse, { weekStartsOn: 1 });
  });

  useEffect(() => {
    const dateToUse = selectedDate.includes('T') ? new Date(selectedDate) : new Date(selectedDate + 'T00:00:00');
    const weekStartOfSelected = startOfWeek(dateToUse, { weekStartsOn: 1 });
    const displayedWeekEnd = addDays(selectedWeekStart, 6);
    const isWithinDisplayedWeek = dateToUse >= selectedWeekStart && dateToUse <= displayedWeekEnd;
    if (!isWithinDisplayedWeek) {
      setSelectedWeekStart(weekStartOfSelected);
    }
  }, [selectedDate]);

  const daysInWeek = Array.from({ length: 7 }).map((_, idx) => addDays(selectedWeekStart, idx));

  const handlePrevWeek = () => setSelectedWeekStart((prev) => subDays(prev, 7));
  const handleNextWeek = () => setSelectedWeekStart((prev) => addDays(prev, 7));

  // ── Telemetry card helpers ─────────────────────────────────────────────────

  /** Rolling 4-week average volume from completed sessions */
  const avgVolume = (() => {
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
    const recent = sessions.filter((s) => !!s.completedAt && new Date(s.date) >= fourWeeksAgo);
    if (recent.length === 0) return 0;
    const total = recent.reduce((acc, s) =>
      acc + (s.plannedExercises as any[]).reduce((a: number, ex: any) =>
        a + (ex.sets as any[]).filter((set: any) => set.completed).reduce((v: number, set: any) => v + (set.weight || 0) * (set.reps || 0), 0), 0), 0);
    return total / recent.length;
  })();

  /** Returns intensity level for a given volume value */
  const getIntensityLevel = (vol: number): 'light' | 'normal' | 'high' | 'extreme' => {
    if (avgVolume === 0) return 'normal';
    const ratio = vol / avgVolume;
    if (ratio < 0.6) return 'light';
    if (ratio < 1.2) return 'normal';
    if (ratio < 1.6) return 'high';
    return 'extreme';
  };

  const intensityBar: Record<string, string> = {
    light: 'bg-emerald-300/60',
    normal: 'bg-emerald-500',
    high: 'bg-orange-400',
    extreme: 'bg-red-500',
  };

  /** Derive split tag from session name */
  const getMuscleGroupTag = (name: string): { label: string; color: string } | null => {
    const n = name.toLowerCase();
    if (n.includes('push') || n.includes('chest') || n.includes('shoulder') || n.includes('tricep')) return { label: 'PUSH', color: 'bg-orange-400/20 text-orange-300 border-orange-400/40' };
    if (n.includes('pull') || n.includes('back') || n.includes('bicep') || n.includes('row')) return { label: 'PULL', color: 'bg-blue-400/20 text-blue-300 border-blue-400/40' };
    if (n.includes('leg') || n.includes('squat') || n.includes('deadlift') || n.includes('glute') || n.includes('quad') || n.includes('hamstring')) return { label: 'LEGS', color: 'bg-red-400/20 text-red-300 border-red-400/40' };
    if (n.includes('arm') || n.includes('curl') || n.includes('press') || n.includes('military') || n.includes('delt')) return { label: 'ARMS', color: 'bg-teal-400/20 text-teal-300 border-teal-400/40' };
    if (n.includes('full') || n.includes('total') || n.includes('body')) return { label: 'FULL', color: 'bg-purple-400/20 text-purple-300 border-purple-400/40' };
    return null;
  };

  /**
   * Single source of truth for workout type → background tint color.
   * MUST match the splitStyle color map in the 7-Day Split boxes below.
   * Order matters: check more specific terms first.
   */
  const getWorkoutTypeTint = (name: string): string | null => {
    const n = name.toLowerCase();
    if (n.includes('leg') || n.includes('squat') || n.includes('deadlift') || n.includes('glute') || n.includes('quad') || n.includes('hamstring'))
      return 'rgba(239, 68, 68, 0.12)';   // Legs — red  (matches splitStyle red)
    // Push/Chest/Shoulders: shoulder is included here (same as getMuscleGroupTag)
    if (n.includes('push') || n.includes('chest') || n.includes('shoulder') || n.includes('tricep') || n.includes('delt') || n.includes('military'))
      return 'rgba(249, 115, 22, 0.12)';  // Push/Chest/Shoulders — orange  (matches splitStyle orange)
    if (n.includes('pull') || n.includes('back') || n.includes('bicep') || n.includes('row'))
      return 'rgba(59, 130, 246, 0.12)';  // Pull/Back — blue  (matches splitStyle blue)
    if (n.includes('arm') || n.includes('curl'))
      return 'rgba(20, 184, 166, 0.12)';  // Arms — teal  (matches splitStyle teal fallback)
    if (n.includes('upper'))
      return 'rgba(234, 179, 8, 0.12)';   // Upper — yellow  (matches splitStyle yellow)
    if (n.includes('lower'))
      return 'rgba(139, 92, 246, 0.12)';  // Lower — purple  (matches splitStyle purple)
    if (n.includes('full') || n.includes('total') || n.includes('body'))
      return 'rgba(34, 197, 94, 0.12)';   // Full Body — green  (matches splitStyle green)
    if (n.includes('rest'))
      return 'rgba(100, 116, 139, 0.08)'; // Rest — dark neutral
    return null; // Custom/unknown — no tint
  };

  /** Map a split category string to a ForgeColor key */
  const getSplitForgeColor = (category: string): ForgeColor => {
    const c = category.toLowerCase();
    if (c.includes('leg') || c.includes('squat') || c.includes('deadlift') || c.includes('glute') || c.includes('quad') || c.includes('hamstring')) return 'legs';
    if (c.includes('push') || c.includes('chest') || c.includes('tricep') || c.includes('shoulder') || c.includes('delt') || c.includes('military')) return 'chest';
    if (c.includes('pull') || c.includes('back') || c.includes('bicep') || c.includes('row')) return 'pull';
    if (c.includes('upper')) return 'orange';
    if (c.includes('lower')) return 'blue';
    if (c.includes('full') || c.includes('total') || c.includes('body')) return 'green';
    if (c.includes('arm') || c.includes('curl')) return 'arms-shoulders';
    if (c.includes('core') || c.includes('cardio') || c.includes('abs')) return 'core';
    if (c === 'rest') return 'rest';
    return 'grey';
  };

  /** Check if any set in a session has a PR */
  const sessionHasPR = (session: WorkoutSession): boolean =>
    (session.plannedExercises as any[]).some((ex: any) =>
      (ex.sets as any[]).some((set: any) => set.isPR === true || set.isNewPR === true));

  /** Map sessionFeeling to emoji */
  const feelingEmoji: Record<string, string> = {
    crushed_it: '🔥',
    solid: '✅',
    tough: '😤',
    cooked: '💀',
  };

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Mobile Weekly Carousel View */}
      <div className="md:hidden forge-panel p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4 text-primary" />
              <span className="font-mono text-xs font-bold uppercase text-primary truncate">
                {format(selectedWeekStart, 'MMM d')} - {format(addDays(selectedWeekStart, 6), 'MMM d, yyyy')}
              </span>
            </div>
            <div className="flex gap-1 mt-0.5 self-start flex-wrap">
              <Button variant="outline" size="sm" onClick={handleCopyLastWeek} className="h-5 px-1.5 font-mono text-[9px] border-primary/40 text-primary hover:bg-primary/10">
                COPY LAST WEEK
              </Button>
              <Button variant="outline" size="sm" onClick={handleGenerateWeekFromSplit} className="h-5 px-1.5 font-mono text-[9px] border-primary/40 text-primary hover:bg-primary/10">
                GENERATE FROM SPLIT
              </Button>
              {activeSplit.isMesocycle && (
                <Button variant="outline" size="sm" onClick={handleApplyMesocycle} className="h-5 px-1.5 font-mono text-[9px] border-orange-400/40 text-orange-400 hover:bg-orange-400/10">
                  APPLY 4-WEEK MESO
                </Button>
              )}
            </div>
            {activeSplit.isMesocycle && activeSplit.weeks && (() => {
              const weekNum = Math.floor((new Date(selectedDate).getTime() - startOfWeek(new Date(selectedDate + 'T00:00:00'), { weekStartsOn: 1 }).getTime()) / (7 * 24 * 3600 * 1000)) % 4;
              const mesoWeek = activeSplit.weeks[weekNum];
              const intentColors: Record<string, string> = { accumulation: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30', overreach: 'text-orange-400 bg-orange-400/10 border-orange-400/30', deload: 'text-blue-400 bg-blue-400/10 border-blue-400/30', maintenance: 'text-purple-400 bg-purple-400/10 border-purple-400/30' };
              return mesoWeek ? <span className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded-sm border uppercase tracking-wider mt-0.5 self-start ${intentColors[mesoWeek.intent] ?? ''}`}>W{mesoWeek.weekNumber}: {mesoWeek.intent}</span> : null;
            })()}
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" onClick={handlePrevWeek} className="h-7 w-7 border-border bg-secondary text-foreground hover:bg-primary">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleNextWeek} className="h-7 w-7 border-border bg-secondary text-foreground hover:bg-primary">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1.5 text-center">
          {daysInWeek.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isSelected = dateStr === selectedDate;
            const session = getSessionForDate(day);
            const isCompleted = session && !!session.completedAt;
            const isPlanned = session && !session.completedAt;
            const today = isToday(day);

            const isPastDay = day < new Date(new Date().setHours(0,0,0,0));
            let borderClass = 'border-border/40';
            let bgClass = isPastDay ? 'bg-[#F3F4F6]/5' : 'bg-secondary/20';
            let textClass = isPastDay ? 'text-muted-foreground/50' : 'text-foreground/80';

            if (today) borderClass = 'border-primary'; // will be overridden inline if split color available
            if (isSelected) {
              bgClass = 'bg-primary/20';
              borderClass = 'border-primary ring-1 ring-primary';
              textClass = 'text-primary font-bold';
            }

            // Compute quick stats for completed sessions
            const completedSets = isCompleted
              ? (session!.plannedExercises as any[]).reduce((acc: number, ex: any) => acc + (ex.sets as any[]).filter((s: any) => s.completed).length, 0)
              : 0;
            const mobileVolume = isCompleted
              ? (session!.plannedExercises as any[]).reduce((acc: number, ex: any) =>
                  acc + (ex.sets as any[]).filter((s: any) => s.completed).reduce((a: number, s: any) => a + (s.weight || 0) * (s.reps || 0), 0), 0)
              : 0;
            const hasCal = isCompleted && !!(session as any)?.watchCalories;
            const hasHr = isCompleted && !!(session as any)?.watchAvgHr;

            // Mobile telemetry card helpers
            const mobileTag = isCompleted ? getMuscleGroupTag(session!.name) : null;
            const mobilePR = isCompleted ? sessionHasPR(session!) : false;
            const mobileFeeling = isCompleted ? (session as any)?.sessionFeeling as string | null : null;
            const mobileIntensity = isCompleted && mobileVolume > 0 ? getIntensityLevel(mobileVolume) : null;
            // Rest day detection: no session and split marks this weekday as rest
            const dayOfWeekIdx = day.getDay() === 0 ? 6 : day.getDay() - 1; // Mon=0
            const splitDay = activeSplit.days[dayOfWeekIdx];
            const isRestDay = !session && splitDay?.category?.toLowerCase() === 'rest';
            // Workout type tint: derived from session name (completed) or split category (planned/rest)
            const mobileTintName = session?.name ?? (isRestDay ? 'rest' : splitDay?.category ?? '');
            const mobileTint = getWorkoutTypeTint(mobileTintName);

            // Split label: use ForgeColor system
            const mobileSplitCat = splitDay?.category?.toLowerCase() ?? '';
            const mobileSplitForgeColor: ForgeColor | null = (splitDay && mobileSplitCat !== 'rest') ? getSplitForgeColor(splitDay.category) : null;
            const mobileSplitLabel = splitDay && mobileSplitCat !== 'rest' ? splitDay.category : null;
            // Today's card border color from split
            const todaySplitBorderCss = (today && mobileSplitForgeColor) ? forgeColor(mobileSplitForgeColor).cssVar : null;

            // Long-press state for this card
            const longPressTimerRef = { current: null as ReturnType<typeof setTimeout> | null };
            const longPressActiveRef = { current: false };

            const handleLongPressStart = (e: React.TouchEvent | React.MouseEvent) => {
              longPressActiveRef.current = false;
              longPressTimerRef.current = setTimeout(() => {
                longPressActiveRef.current = true;
                const sess = session ? {
                  ...session,
                  sessionFeeling: (session as any).sessionFeeling ?? null,
                  recoveryNudge: (session as any).recoveryNudge ?? null,
                } : null;
                openDayStats(dateStr, sess);
              }, 400);
            };

            const handleLongPressEnd = () => {
              if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
            };

            const handleLongPressClick = (e: React.MouseEvent) => {
              if (longPressActiveRef.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              handleDayClick(day);
            };

            const handleContextMenu = (e: React.MouseEvent) => {
              e.preventDefault();
              const sess = session ? {
                ...session,
                sessionFeeling: (session as any).sessionFeeling ?? null,
                recoveryNudge: (session as any).recoveryNudge ?? null,
              } : null;
              openDayStats(dateStr, sess);
            };

            return (
              <button
                key={dateStr}
                onClick={handleLongPressClick}
                onTouchStart={handleLongPressStart}
                onTouchEnd={handleLongPressEnd}
                onMouseDown={handleLongPressStart}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                onContextMenu={handleContextMenu}
                className={`relative rounded-sm border ${borderClass} ${
                  isRestDay ? 'bg-secondary/5 border-border/20' : bgClass
                } ${textClass} flex flex-col items-start justify-between transition-all overflow-hidden select-none`}
                style={{ minHeight: 72, padding: '5px 5px 8px 5px', backgroundColor: mobileTint ?? undefined, ...(today && todaySplitBorderCss && !isSelected ? { borderColor: todaySplitBorderCss, borderWidth: '2px' } : {}) }}
              >
                {/* Top row: day label + feeling emoji */}
                <div className="flex items-start justify-between w-full gap-0.5">
                  <div className="flex flex-col items-start leading-none">
                    <span className="text-[8px] font-mono uppercase text-muted-foreground">{format(day, 'EEE').substring(0, 3)}</span>
                    <span className="text-[11px] font-mono font-bold mt-0.5">{format(day, 'd')}</span>
                  </div>
                  {mobileFeeling && (
                    <span className="text-[10px] leading-none mt-0.5" title={mobileFeeling.replace('_', ' ')}>
                      {feelingEmoji[mobileFeeling] ?? ''}
                    </span>
                  )}
                  {mobilePR && !mobileFeeling && (
                    <span title="PR Hit!">
                      <Zap className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400 mt-0.5 shrink-0" />
                    </span>
                  )}
                  {isRestDay && <Moon className="h-2.5 w-2.5 text-muted-foreground/50 mt-0.5 shrink-0" />}
                </div>

                {/* Split muscle group pill */}
                {mobileSplitLabel && mobileSplitForgeColor && (
                  <div className={`w-full ${isCompleted ? 'opacity-50' : 'opacity-90'}`}>
                    <ForgePill
                      color={mobileSplitForgeColor}
                      label={mobileSplitLabel.length > 6 ? mobileSplitLabel.slice(0, 5) + '…' : mobileSplitLabel}
                      size="xs"
                      className="text-[6px] px-1 py-0"
                    />
                  </div>
                )}

                {/* Center: sets + volume */}
                {isCompleted && (
                  <div className="flex flex-col items-start gap-0 mt-0.5 w-full">
                    {completedSets > 0 && (
                      <span className="text-[7px] font-mono text-success leading-none">{completedSets}S</span>
                    )}
                    {mobileVolume > 0 && (
                      <span className="text-[7px] font-mono text-primary/70 leading-none">{(mobileVolume / 1000).toFixed(1)}k</span>
                    )}
                  </div>
                )}

                {/* Lower: watch icons + muscle tag + PR */}
                {isCompleted && (
                  <div className="flex items-center gap-0.5 w-full mt-0.5 flex-wrap">
                    {hasCal && <Flame className="h-2 w-2 text-orange-400 shrink-0" />}
                    {hasHr && <Heart className="h-2 w-2 text-red-400 shrink-0" />}
                    {/* pill label removed — tint communicates workout type */}
                    {mobilePR && (
                      <span title="PR Hit!">
                        <Zap className="h-2 w-2 text-yellow-400 fill-yellow-400 shrink-0" />
                      </span>
                    )}
                  </div>
                )}

                {isPlanned && !isCompleted && (
                  <span className="w-1 h-1 rounded-full bg-primary forge-glow-green mt-auto" />
                )}

                {/* Macro totals if nutrition was logged */}
                {(() => {
                  const macros = monthNutrition[dateStr];
                  if (!macros) return null;
                  const pPct = Math.min(Math.round((macros.protein / macroTargets.protein) * 100), 100);
                  const cPct = Math.min(Math.round((macros.carbs / macroTargets.carbs) * 100), 100);
                  const fPct = Math.min(Math.round((macros.fat / macroTargets.fat) * 100), 100);
                  return (
                    <div className="flex flex-col gap-0.5 w-full mt-0.5">
                      <div className="flex items-center gap-0.5">
                        <span className="text-[6px] font-mono text-blue-400 leading-none">{macros.protein}P</span>
                        <span className="text-[5px] font-mono text-muted-foreground/40">·</span>
                        <span className="text-[6px] font-mono text-yellow-400 leading-none">{macros.carbs}C</span>
                        <span className="text-[5px] font-mono text-muted-foreground/40">·</span>
                        <span className="text-[6px] font-mono text-red-400 leading-none">{macros.fat}F</span>
                      </div>
                      <div className="flex flex-col gap-[1px] w-full">
                        <div className="h-[2px] bg-secondary/40 rounded-full overflow-hidden"><div className="h-full bg-blue-400 rounded-full" style={{ width: `${pPct}%` }} /></div>
                        <div className="h-[2px] bg-secondary/40 rounded-full overflow-hidden"><div className="h-full bg-yellow-400 rounded-full" style={{ width: `${cPct}%` }} /></div>
                        <div className="h-[2px] bg-secondary/40 rounded-full overflow-hidden"><div className="h-full bg-red-400 rounded-full" style={{ width: `${fPct}%` }} /></div>
                      </div>
                    </div>
                  );
                })()}

                {isRestDay && (
                  <span className="text-[6px] font-mono text-muted-foreground/40 uppercase tracking-wider mt-auto">REST</span>
                )}

                {/* Intensity bar — absolute bottom edge */}
                {mobileIntensity && (
                  <div className={`absolute bottom-0 left-0 right-0 h-[3px] ${intensityBar[mobileIntensity]} rounded-b-sm`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop Calendar Grid */}
      <div className={`hidden md:block forge-panel ${fullWidth ? 'p-6' : 'p-4'}`}>
        <div className="forge-header">
          <span>Log Timeline</span>
          <div className="flex items-center gap-3">
            {streak > 0 && (
              <div className="flex items-center gap-1 text-xs font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-sm animate-pulse" title="Weekly Consistency Streak (3+ workouts/week)">
                <Flame className="h-3.5 w-3.5 text-primary fill-primary" />
                <span>{streak} WEEK STREAK</span>
              </div>
            )}
            <span className="text-primary font-mono flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" /> TIMELINE
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-primary">{format(currentMonth, 'MMMM yyyy')}</h3>
            <div className="flex gap-1.5 flex-wrap items-center">
              <Button variant="outline" size="sm" onClick={handleCopyLastWeek} className="h-6 px-2 font-mono text-[10px] border-primary/40 text-primary hover:bg-primary/10" title="Copy planned workouts from last week to this week">
                COPY LAST WEEK
              </Button>
              <Button variant="outline" size="sm" onClick={handleGenerateWeekFromSplit} className="h-6 px-2 font-mono text-[10px] border-primary/40 text-primary hover:bg-primary/10" title="Generate empty workouts for this week matching your active split targets">
                GENERATE FROM SPLIT
              </Button>
              {activeSplit.isMesocycle && (
                <Button variant="outline" size="sm" onClick={handleApplyMesocycle} className="h-6 px-2 font-mono text-[10px] border-orange-400/40 text-orange-400 hover:bg-orange-400/10" title="Apply full 4-week mesocycle starting from this week">
                  APPLY 4-WEEK MESO
                </Button>
              )}
              {activeSplit.isMesocycle && activeSplit.weeks && (() => {
                const weekNum = Math.floor((new Date(selectedDate).getTime() - startOfWeek(new Date(selectedDate + 'T00:00:00'), { weekStartsOn: 1 }).getTime()) / (7 * 24 * 3600 * 1000)) % 4;
                const mesoWeek = activeSplit.weeks[weekNum];
                const intentColors: Record<string, string> = { accumulation: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30', overreach: 'text-orange-400 bg-orange-400/10 border-orange-400/30', deload: 'text-blue-400 bg-blue-400/10 border-blue-400/30', maintenance: 'text-purple-400 bg-purple-400/10 border-purple-400/30' };
                return mesoWeek ? <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${intentColors[mesoWeek.intent] ?? ''}`}>W{mesoWeek.weekNumber}: {mesoWeek.intent}</span> : null;
              })()}
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" onClick={handlePrevMonth} className="h-7 w-7 border-border bg-secondary text-foreground hover:bg-primary hover:text-primary-foreground">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleNextMonth} className="h-7 w-7 border-border bg-secondary text-foreground hover:bg-primary hover:text-primary-foreground">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {weekdayHeaders.map((day) => (
            <div key={day} className="text-[10px] font-mono font-bold text-muted-foreground py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {paddingDays.map((_, idx) => (
            <div key={`pad-${idx}`} className="aspect-square bg-transparent" />
          ))}

          {daysInMonth.map((day: Date) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isSelected = dateStr === selectedDate;
            const session = getSessionForDate(day);
            const isCompleted = session && !!session.completedAt;
            const isPlanned = session && !session.completedAt;
            const today = isToday(day);

            let borderClass = 'border-border/50';
            let bgClass = 'bg-secondary/40';
            let textClass = 'text-foreground/80';

            if (today) borderClass = 'border-primary';
            if (isSelected) {
              bgClass = 'bg-primary/20';
              borderClass = 'border-primary ring-1 ring-primary';
              textClass = 'text-primary font-bold';
            }

            // Compute quick stats for desktop month cells
            const desktopSets = isCompleted
              ? (session!.plannedExercises as any[]).reduce((acc: number, ex: any) => acc + (ex.sets as any[]).filter((s: any) => s.completed).length, 0)
              : 0;
            const desktopVolume = isCompleted
              ? (session!.plannedExercises as any[]).reduce((acc: number, ex: any) =>
                  acc + (ex.sets as any[]).filter((s: any) => s.completed).reduce((a: number, s: any) => a + (s.weight || 0) * (s.reps || 0), 0), 0)
              : 0;
            const desktopCal = isCompleted ? (session as any)?.watchCalories : null;
            const desktopHr = isCompleted ? (session as any)?.watchAvgHr : null;

            // Desktop telemetry card helpers
            const desktopTag = isCompleted ? getMuscleGroupTag(session!.name) : null;
            const desktopPR = isCompleted ? sessionHasPR(session!) : false;
            const desktopFeeling = isCompleted ? (session as any)?.sessionFeeling as string | null : null;
            const desktopIntensity = isCompleted && desktopVolume > 0 ? getIntensityLevel(desktopVolume) : null;
            // Rest day detection for desktop
            const deskDayOfWeekIdx = day.getDay() === 0 ? 6 : day.getDay() - 1;
            const deskSplitDay = activeSplit.days[deskDayOfWeekIdx];
            const deskIsRestDay = !session && deskSplitDay?.category?.toLowerCase() === 'rest';
            // Workout type tint for desktop card
            const desktopTintName = session?.name ?? (deskIsRestDay ? 'rest' : deskSplitDay?.category ?? '');
            const desktopTint = getWorkoutTypeTint(desktopTintName);

            // Desktop long-press / right-click state
            const deskLongPressTimer = { current: null as ReturnType<typeof setTimeout> | null };
            const deskLongPressActive = { current: false };

            const handleDeskLongPressStart = () => {
              deskLongPressActive.current = false;
              deskLongPressTimer.current = setTimeout(() => {
                deskLongPressActive.current = true;
                const sess = session ? {
                  ...session,
                  sessionFeeling: (session as any).sessionFeeling ?? null,
                  recoveryNudge: (session as any).recoveryNudge ?? null,
                } : null;
                openDayStats(dateStr, sess);
              }, 400);
            };

            const handleDeskLongPressEnd = () => {
              if (deskLongPressTimer.current) clearTimeout(deskLongPressTimer.current);
            };

            const handleDeskClick = (e: React.MouseEvent) => {
              if (deskLongPressActive.current) { e.preventDefault(); return; }
              handleDayClick(day);
            };

            const handleDeskContextMenu = (e: React.MouseEvent) => {
              e.preventDefault();
              const sess = session ? {
                ...session,
                sessionFeeling: (session as any).sessionFeeling ?? null,
                recoveryNudge: (session as any).recoveryNudge ?? null,
              } : null;
              openDayStats(dateStr, sess);
            };

            return (
              <button
                key={dateStr}
                onClick={handleDeskClick}
                onMouseDown={handleDeskLongPressStart}
                onMouseUp={handleDeskLongPressEnd}
                onMouseLeave={handleDeskLongPressEnd}
                onContextMenu={handleDeskContextMenu}
                className={`group ${
                  fullWidth ? 'min-h-[90px]' : 'min-h-[72px]'
                } p-1.5 rounded-sm border ${borderClass} ${
                  deskIsRestDay ? 'bg-secondary/5 border-border/20' : bgClass
                } ${textClass} flex flex-col justify-between items-start transition-all hover:border-primary/70 relative overflow-hidden w-full select-none`}
                style={{ backgroundColor: desktopTint ?? undefined }}
              >
                {/* Top row: date number + feeling emoji */}
                <div className="flex items-start justify-between w-full">
                  <span className={`font-mono ${fullWidth ? 'text-sm' : 'text-xs'}`}>{format(day, 'd')}</span>
                  <div className="flex items-center gap-0.5">
                    {/* Hover expand icon — desktop only */}
                    <span
                      className="hidden group-hover:flex items-center justify-center h-3.5 w-3.5 rounded-sm bg-primary/20 hover:bg-primary/40 text-primary transition-all cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); const sess = session ? { ...session, sessionFeeling: (session as any).sessionFeeling ?? null, recoveryNudge: (session as any).recoveryNudge ?? null } : null; openDayStats(dateStr, sess); }}
                      title="View full day stats"
                    >
                      <Eye className="h-2 w-2" />
                    </span>
                    {desktopFeeling && (
                      <span className="text-[10px] leading-none" title={desktopFeeling.replace('_', ' ')}>
                        {feelingEmoji[desktopFeeling] ?? ''}
                      </span>
                    )}
                    {desktopPR && (
                      <span title="PR Hit!">
                        <Zap className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />
                      </span>
                    )}
                    {deskIsRestDay && <Moon className="h-2.5 w-2.5 text-muted-foreground/40" />}
                  </div>
                </div>

                {/* Center: sets + volume */}
                {isCompleted && (
                  <div className="flex flex-col gap-0 w-full">
                    {desktopSets > 0 && (
                      <span className={`font-mono text-success leading-none ${fullWidth ? 'text-[9px]' : 'text-[7px]'}`}>{desktopSets} sets</span>
                    )}
                    {desktopVolume > 0 && (
                      <span className={`font-mono text-primary/70 leading-none ${fullWidth ? 'text-[9px]' : 'text-[7px]'}`}>{(desktopVolume / 1000).toFixed(1)}k lbs</span>
                    )}
                  </div>
                )}

                {/* Lower: watch data + muscle pill */}
                {isCompleted && (
                  <div className="flex items-center gap-0.5 w-full flex-wrap">
                    {desktopCal && (
                      <span className={`flex items-center gap-0.5 font-mono text-orange-400 ${fullWidth ? 'text-[8px]' : 'text-[7px]'}`}>
                        <Flame className="h-2 w-2" />{desktopCal}
                      </span>
                    )}
                    {desktopHr && (
                      <span className={`flex items-center gap-0.5 font-mono text-red-400 ${fullWidth ? 'text-[8px]' : 'text-[7px]'}`}>
                        <Heart className="h-2 w-2" />{desktopHr}
                      </span>
                    )}
                    {/* pill label removed — tint communicates workout type */}
                  </div>
                )}

                {isPlanned && !isCompleted && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary forge-glow-green" />
                )}

                {/* Macro totals if nutrition was logged */}
                {(() => {
                  const macros = monthNutrition[dateStr];
                  if (!macros) return null;
                  const pPct = Math.min(Math.round((macros.protein / macroTargets.protein) * 100), 100);
                  const cPct = Math.min(Math.round((macros.carbs / macroTargets.carbs) * 100), 100);
                  const fPct = Math.min(Math.round((macros.fat / macroTargets.fat) * 100), 100);
                  const sz = fullWidth ? 'text-[8px]' : 'text-[6px]';
                  return (
                    <div className="flex flex-col gap-0.5 w-full">
                      <div className="flex items-center gap-0.5">
                        <span className={`font-mono text-blue-400 leading-none ${sz}`}>{macros.protein}P</span>
                        <span className="text-[5px] font-mono text-muted-foreground/40">·</span>
                        <span className={`font-mono text-yellow-400 leading-none ${sz}`}>{macros.carbs}C</span>
                        <span className="text-[5px] font-mono text-muted-foreground/40">·</span>
                        <span className={`font-mono text-red-400 leading-none ${sz}`}>{macros.fat}F</span>
                      </div>
                      <div className="flex flex-col gap-[1px] w-full">
                        <div className="h-[2px] bg-secondary/40 rounded-full overflow-hidden"><div className="h-full bg-blue-400 rounded-full" style={{ width: `${pPct}%` }} /></div>
                        <div className="h-[2px] bg-secondary/40 rounded-full overflow-hidden"><div className="h-full bg-yellow-400 rounded-full" style={{ width: `${cPct}%` }} /></div>
                        <div className="h-[2px] bg-secondary/40 rounded-full overflow-hidden"><div className="h-full bg-red-400 rounded-full" style={{ width: `${fPct}%` }} /></div>
                      </div>
                    </div>
                  );
                })()}

                {deskIsRestDay && (
                  <span className="text-[6px] font-mono text-muted-foreground/30 uppercase tracking-wider">REST</span>
                )}

                {/* Intensity bar — absolute bottom edge */}
                {desktopIntensity && (
                  <div className={`absolute bottom-0 left-0 right-0 h-[3px] ${intensityBar[desktopIntensity]}`} />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex gap-3 mt-4 pt-3 border-t border-border/40 text-[9px] font-mono text-muted-foreground justify-center flex-wrap">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-success forge-glow-green" /> Completed
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-primary forge-glow-green" /> Planned
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full border border-primary" /> Today
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-1 rounded-sm bg-emerald-300/60" /> Light
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-1 rounded-sm bg-emerald-500" /> Normal
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-1 rounded-sm bg-orange-400" /> High
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-1 rounded-sm bg-red-500" /> Extreme
          </div>
          <div className="flex items-center gap-1">
            <Zap className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" /> PR
          </div>
          <div className="flex items-center gap-1">
            <Moon className="h-2.5 w-2.5 text-muted-foreground/50" /> Rest
          </div>
        </div>
      </div>

      {/* Split Telemetry & Recent Sessions Panel */}
      <div className={`mt-4 ${fullWidth ? 'grid grid-cols-2 gap-4' : 'space-y-4'}`}>
        <div className="forge-panel p-4">
          <div className="forge-header flex justify-between items-center">
            <div className="flex items-center gap-2 flex-wrap">
              <ForgeSectionHeader label="7-Day Split" />
              <span className="font-mono text-xs font-bold text-primary">{activeSplit.name.split(' (')[0]}</span>
              {/* Mesocycle week tag as ForgePill */}
              {activeSplit.isMesocycle && activeSplit.weeks && (() => {
                const isoWeek = Math.ceil((((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000) + new Date(new Date().getFullYear(), 0, 1).getDay() + 1) / 7);
                const weekIdx = (isoWeek - 1) % 4;
                const mesoWeek = activeSplit.weeks[weekIdx];
                if (!mesoWeek) return null;
                const intentColor: ForgeColor = mesoWeek.intent === 'overreach' ? 'orange' : mesoWeek.intent === 'deload' ? 'blue' : mesoWeek.intent === 'maintenance' ? 'purple' : 'green';
                return <ForgePill color={intentColor} label={`W${mesoWeek.weekNumber ?? weekIdx + 1}: ${mesoWeek.intent}`} size="xs" />;
              })()}
            </div>
            <SplitManager onSplitChange={handleSplitChange} />
          </div>

        </div>

        {/* Recent Sessions Feed */}
        <div className="forge-panel p-4">
          <div className="forge-header">
            <span>Recent Session Feed</span>
            <span className="text-primary font-mono text-[10px]">LAST 3 LOGS</span>
          </div>

          <div className="space-y-2 mt-2">
            {sessions
              .filter((s) => !!s.completedAt)
              .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))
              .slice(0, 3)
              .map((session) => {
                let totalTonnage = 0;
                let totalSets = 0;
                (session.plannedExercises as any[]).forEach((ex: any) => {
                  (ex.sets as any[]).forEach((set: any) => {
                    if (set.completed) {
                      totalTonnage += (set.weight || 0) * (set.reps || 0);
                      totalSets++;
                    }
                  });
                });

                return (
                  <div key={session.id} className="p-2.5 bg-secondary/20 border border-border/60 rounded-sm flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <h4 className="font-bold text-xs truncate">{session.name}</h4>
                          {/* Tracking mode mini-badge */}
                          {(() => {
                            const mode = (session as any).trackingMode ?? 'overload';
                            if (mode === 'hypertrophy') return <span className="text-[7px] font-mono font-bold bg-emerald-400/10 text-emerald-400 border border-emerald-400/30 px-1 py-0.5 rounded-sm uppercase tracking-wider shrink-0">H</span>;
                            if (mode === 'deload') return <span className="text-[7px] font-mono font-bold bg-blue-400/10 text-blue-400 border border-blue-400/30 px-1 py-0.5 rounded-sm uppercase tracking-wider shrink-0">D</span>;
                            return <span className="text-[7px] font-mono font-bold bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-1 py-0.5 rounded-sm uppercase tracking-wider shrink-0">O</span>;
                          })()}
                          {/* Session feeling emoji */}
                          {(() => {
                            const feeling = (session as any).sessionFeeling as string | null;
                            if (!feeling) return null;
                            const emojiMap: Record<string, string> = {
                              crushed_it: '🔥', solid: '✅', tough: '😤', cooked: '💀',
                            };
                            return <span className="text-sm leading-none" title={feeling.replace('_', ' ')}>{emojiMap[feeling] ?? ''}</span>;
                          })()}
                        </div>
                        <p className="text-[9px] font-mono text-muted-foreground uppercase mt-0.5">{new Date(session.completedAt!).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="font-mono text-xs font-bold text-primary">
                            {totalTonnage.toLocaleString()} <span className="text-[9px] text-muted-foreground">LBS</span>
                          </div>
                          <div className="font-mono text-[9px] text-muted-foreground uppercase">{totalSets} SETS HIT</div>
                        </div>
                        <div className="flex gap-0.5 border-l border-border/40 pl-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingSessionId(session.id);
                              setIsEditOpen(true);
                            }}
                            className="h-7 w-7 text-muted-foreground hover:text-primary"
                            title="Edit Session"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteSession(session.id, session.name)} className="h-7 w-7 text-muted-foreground hover:text-destructive" title="Delete Session">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Skipped exercises row */}
                    {(() => {
                      const skipped = (session.plannedExercises as any[]).filter((pe: any) => pe.skipped);
                      if (skipped.length === 0) return null;
                      return (
                        <div className="flex items-start gap-1.5 pt-0.5 border-t border-orange-500/20">
                          <span className="text-[9px] font-mono text-orange-400/70 shrink-0 mt-0.5">⚡ SKIPPED:</span>
                          <div className="flex flex-wrap gap-1">
                            {skipped.map((pe: any, i: number) => {
                              const ex = exercises.find((e) => e.id === pe.exerciseId);
                              return (
                                <span key={i} className="text-[9px] font-mono text-orange-400/80 bg-orange-500/5 border border-orange-500/20 px-1 py-0.5 rounded-sm" title={pe.skipReason}>
                                  {ex?.name ?? pe.exerciseId}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Watch biometrics row */}
                    {(session.watchCalories || session.watchAvgHr) && (
                      <div className="flex items-center gap-3 pt-0.5 border-t border-border/30">
                        {session.watchCalories && (
                          <span className="flex items-center gap-1 text-[9px] font-mono text-orange-400">
                            <Flame className="h-3 w-3" />
                            {session.watchCalories} CAL
                          </span>
                        )}
                        {session.watchAvgHr && (
                          <span className="flex items-center gap-1 text-[9px] font-mono text-red-400">
                            <Heart className="h-3 w-3" />
                            {session.watchAvgHr}{session.watchMaxHr ? `/${session.watchMaxHr}` : ""} BPM
                          </span>
                        )}
                        <span className="text-[9px] font-mono text-muted-foreground/50 flex items-center gap-1">
                          <Activity className="h-2.5 w-2.5" />
                          WATCH
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

            {sessions.filter((s) => !!s.completedAt).length === 0 && (
              <div className="text-center py-6 border border-dashed border-border/50 rounded-sm">
                <p className="text-[10px] font-mono text-muted-foreground">No completed sessions yet.</p>
                <p className="text-[8px] text-muted-foreground/70 mt-0.5">Your finished workouts will show up here!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Completed Session Dialog */}
      <EditCompletedSessionDialog
        sessionId={editingSessionId}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingSessionId(null);
        }}
        onSave={() => {
          utils.sessions.list.invalidate();
          utils.sessions.byDate.invalidate();
          utils.prs.list.invalidate();
        }}
      />

      {/* Day-Action Dialog */}
      <Dialog open={!!dayActionDate} onOpenChange={(open) => { if (!open) { setDayActionDate(null); setDayActionSession(null); } }}>
        <DialogContent className="max-w-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm uppercase tracking-wider text-primary flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {dayActionDate ? format(new Date(dayActionDate + 'T00:00:00'), 'EEEE, MMM d') : ''}
            </DialogTitle>
          </DialogHeader>

          {dayActionSession && (
            <div className="space-y-3">
              {/* Session summary */}
              <div className="bg-secondary/40 border border-border/60 rounded-sm p-3 space-y-1">
                <div className="flex items-center gap-2">
                  {dayActionSession.completedAt ? (
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-primary shrink-0" />
                  )}
                  <span className="font-mono text-sm font-bold text-foreground truncate">{dayActionSession.name}</span>
                </div>
                <div className="text-[11px] font-mono text-muted-foreground pl-6">
                  {dayActionSession.completedAt ? 'COMPLETED' : 'PLANNED'}
                  {' · '}
                  {(dayActionSession.plannedExercises as any[]).length} exercise{(dayActionSession.plannedExercises as any[]).length !== 1 ? 's' : ''}
                </div>
                {/* Muscle group summary — clickable to filter history */}
                {(() => {
                  const pes = dayActionSession.plannedExercises as any[];
                  const muscleGroups = Array.from(new Set(
                    pes
                      .map((pe: any) => exercises.find((e) => e.id === pe.exerciseId)?.category)
                      .filter(Boolean)
                  )) as string[];
                  if (muscleGroups.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1 pl-6 pt-1">
                      {muscleGroups.map((group) => (
                        <button
                          key={group}
                          type="button"
                          onClick={() => {
                            setMuscleFilterGroup(group);
                            setDayActionDate(null);
                            setDayActionSession(null);
                          }}
                          title={`View all ${group} workouts`}
                          className="text-[10px] font-mono font-bold uppercase bg-primary/10 text-primary border border-primary/25 px-1.5 py-0.5 rounded-sm hover:bg-primary/25 hover:border-primary/50 active:scale-95 transition-all cursor-pointer"
                        >
                          {group}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-1 gap-2">
                <Button
                  className="w-full font-mono text-xs uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2"
                  onClick={() => {
                    onSelectDate(dayActionDate!);
                    setDayActionDate(null);
                    setDayActionSession(null);
                  }}
                >
                  <Eye className="h-4 w-4" />
                  {dayActionSession.completedAt ? 'View Completed Workout' : 'View / Edit Workout'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full font-mono text-xs uppercase tracking-wider border-primary/40 text-primary hover:bg-primary/10 flex items-center gap-2"
                  onClick={() => {
                    const date = dayActionDate!;
                    setDayActionDate(null);
                    setDayActionSession(null);
                    if (onAddNewWorkout) {
                      onAddNewWorkout(date);
                    } else {
                      onSelectDate(date);
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Add Another Workout
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Muscle Group History Dialog */}
      <Dialog open={!!muscleFilterGroup} onOpenChange={(open) => { if (!open) setMuscleFilterGroup(null); }}>
        <DialogContent className="max-w-lg bg-card border-border max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm uppercase tracking-wider text-primary flex items-center gap-2">
              <Activity className="h-4 w-4" />
              {muscleFilterGroup} — Workout History
            </DialogTitle>
          </DialogHeader>

          {(() => {
            if (!muscleFilterGroup) return null;

            // Find all completed sessions that include at least one exercise in this muscle group
            const filteredSessions = sessions
              .filter((s) => {
                if (!s.completedAt) return false;
                const pes = s.plannedExercises as any[];
                return pes.some((pe: any) => {
                  const ex = exercises.find((e) => e.id === pe.exerciseId);
                  return ex?.category === muscleFilterGroup;
                });
              })
              .sort((a, b) => b.date.localeCompare(a.date));

            if (filteredSessions.length === 0) {
              return (
                <div className="py-8 text-center text-muted-foreground font-mono text-sm">
                  No completed workouts found for {muscleFilterGroup}.
                </div>
              );
            }

            return (
              <div className="overflow-y-auto flex-1 space-y-3 pr-1">
                {filteredSessions.map((s) => {
                  const pes = (s.plannedExercises as any[]).filter((pe: any) => {
                    const ex = exercises.find((e) => e.id === pe.exerciseId);
                    return ex?.category === muscleFilterGroup;
                  });
                  return (
                    <div key={s.id} className="bg-secondary/40 border border-border/60 rounded-sm p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-bold text-foreground">{s.name}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{s.date}</span>
                      </div>
                      {pes.map((pe: any) => {
                        const ex = exercises.find((e) => e.id === pe.exerciseId);
                        const completedSets = (pe.sets as any[]).filter((set: any) => set.completed);
                        if (completedSets.length === 0) return null;
                        return (
                          <div key={pe.id} className="pl-2 border-l-2 border-primary/30 space-y-1">
                            <span className="font-mono text-[11px] text-primary font-bold uppercase">{ex?.name ?? pe.exerciseId}</span>
                            <div className="flex flex-wrap gap-1">
                              {completedSets.map((set: any, idx: number) => {
                                const isDrop = set.setType === 'drop_set';
                                const stBadgeCfg = getSetTypeBadgeConfig(set.setType, set.isWarmup);
                                const mainText = set.weight
                                  ? `${set.weight} lbs × ${set.reps}${set.rpe != null ? ` @${set.rpe}` : ''}${set.rir != null ? ` RIR${set.rir}` : ''}`
                                  : set.durationSeconds ? `${set.durationSeconds}s` : `${set.reps} reps`;
                                // Per-set tracking mode dot: green=overload, teal=hypertrophy, grey=deload
                                const sessionMode = (s as any).trackingMode ?? 'overload';
                                const setMode = set.setTrackingMode ?? sessionMode;
                                const modeDot = setMode === 'overload'
                                  ? <span title="Overload" className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />
                                  : setMode === 'hypertrophy'
                                  ? <span title="Hypertrophy" className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                                  : <span title="Deload" className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/60 shrink-0" />;
                                return (
                                  <div key={idx} className="flex flex-col gap-0.5">
                                    <span className="text-[10px] font-mono bg-primary/8 border border-primary/20 text-foreground px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                                      {modeDot}
                                      {stBadgeCfg && <SetTypeBadge setType={set.setType} isWarmup={set.isWarmup} compact />}
                                      SET {idx + 1}: {mainText}
                                    </span>
                                    {isDrop && set.dropSetEntries && set.dropSetEntries.length > 0 && (
                                      <div className="pl-2 flex flex-wrap gap-0.5">
                                        {set.dropSetEntries.map((drop: any, di: number) => (
                                          <span key={di} className="text-[9px] font-mono text-amber-500/80 bg-amber-500/5 border border-amber-500/20 px-1 py-0.5 rounded-sm">
                                            ↓{di + 1}: {drop.weight ?? '?'} lbs × {drop.reps ?? '?'}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Day Stats Modal (long-press / right-click) */}
      <DayStatsModal
        isOpen={!!dayStatsDate}
        onClose={closeDayStats}
        dateStr={dayStatsDate ?? ''}
        session={dayStatsSession}
        macros={dayStatsDate ? (monthNutrition[dayStatsDate] ?? null) : null}
        macroTargets={macroTargets}
        exerciseNames={Object.fromEntries(exercises.map((e) => [e.id, e.name]))}
      />
    </div>
  );
}
